import { Box, Button, Spinner, Text, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useKnowledgeBaseStore } from "../../../hooks/knowledgeBase.hooks";
import { CreateArticleRequest, UpdateArticleRequest } from "../../../models/KnowledgeBaseArticle";
import {
	useCreateArticle,
	useKnowledgeBaseArticles,
	useUpdateArticle,
} from "../../../networking/knowledgeBase.hooks";
import { matchScope } from "../../../utils/string.utils";
import ArticleCreator from "./ArticleCreator";
import ArticleEditor from "./ArticleEditor";
import ArticleViewer from "./ArticleViewer";
import { useAuthStore } from "../../../stores/auth.store";

/**
 * Knowledge base sidebar component.
 * Displays help articles based on tags, with edit capability for editors.
 */
const KnowledgeBaseSidebar: React.FC = () => {
	const { isOpen, tags, side, closeKnowledgeBase } = useKnowledgeBaseStore();
	// When opened on the left it sits over an already-open right-side sheet
	// (the filter sidebar). It stays modal and is raised above that sheet so it
	// is the top layer: its own overlay covers the filter sheet and clicking the
	// overlay closes the KB — not the filter sheet underneath.
	const isSideBySide = side === "left";
	const authStore = useAuthStore();
	const { articles, isLoading } = useKnowledgeBaseArticles(tags, isOpen);
	const updateArticle = useUpdateArticle();
	const createArticle = useCreateArticle();
	const [editingArticleId, setEditingArticleId] = useState<string | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	// Check if user has editor permissions
	const canEdit = matchScope("knowledge-base.*", authStore.scopes);

	const selectedArticle = articles.length > 0 ? articles[0] : null;
	const isEditing = editingArticleId === selectedArticle?.id;

	const handleSave = (updates: UpdateArticleRequest) => {
		if (!selectedArticle) return;

		updateArticle.mutate(
			{
				articleId: selectedArticle.id,
				update: updates,
			},
			{
				onSuccess: () => {
					setEditingArticleId(null);
				},
			}
		);
	};

	const handleCreate = (request: CreateArticleRequest) => {
		createArticle.mutate(request, {
			onSuccess: () => {
				setIsCreating(false);
			},
		});
	};

	return (
		<Sheet
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) closeKnowledgeBase();
			}}
		>
			<SheetContent
				side={side}
				className={cn(
					"w-full sm:max-w-md",
					// Side by side: raise above the filter sheet (z-[1200]) so the KB
					// always stacks on top, and stay narrow so the dimmed filter
					// sheet (sm:max-w-md) remains partly visible behind it.
					isSideBySide && "z-[1300] w-3/4 sm:max-w-sm"
				)}
				overlayClassName={cn(isSideBySide && "z-[1300]")}
				aria-describedby={undefined}
			>
				<SheetHeader className="border-b border-border">
					<SheetTitle>Hjälpcenter</SheetTitle>
				</SheetHeader>

				<div className="flex-1 overflow-y-auto px-6 py-4">
					{/* Loading State */}
					{isLoading && (
						<Box textAlign="center" py={10}>
							<Spinner size="xl" />
							<Text mt={4} color="gray.600">
								Laddar...
							</Text>
						</Box>
					)}

					{/* No Articles Found */}
					{!isLoading && articles.length === 0 && !isCreating && (
						<Box textAlign="center" py={10}>
							{/* <Text fontSize="xl" mb={2}>
								📚
							</Text> */}
							<Text color="gray.600">Hittade inga hjälpartiklar om detta ämne.</Text>
							{canEdit && (
								<>
									<Text fontSize="sm" color="gray.500" mt={2}>
										Som redaktör kan du skapa och redigera artiklar i kunskapsbasen.
									</Text>
									<Button colorScheme="blue" size="sm" mt={4} onClick={() => setIsCreating(true)}>
										Skapa ny artikel
									</Button>
								</>
							)}
						</Box>
					)}

					{/* Create New Article */}
					{!isLoading && isCreating && (
						<Box>
							<ArticleCreator
								initialTags={tags}
								onCreate={handleCreate}
								onCancel={() => setIsCreating(false)}
								isCreating={createArticle.isLoading}
							/>
						</Box>
					)}

					{/* Single Article */}
					{!isLoading && selectedArticle && (
						<Box>
							{isEditing ? (
								<ArticleEditor
									article={selectedArticle}
									onSave={handleSave}
									onCancel={() => setEditingArticleId(null)}
									isSaving={updateArticle.isLoading}
								/>
							) : (
								<ArticleViewer
									article={selectedArticle}
									canEdit={canEdit}
									onEdit={() => setEditingArticleId(selectedArticle.id)}
								/>
							)}
						</Box>
					)}

					{/* Multiple Articles - Show List */}
					{!isLoading && articles.length > 1 && !isEditing && (
						<Box>
							<Text fontSize="lg" fontWeight="bold" mb={4}>
								Hittade flera artiklar:
							</Text>
							<VStack align="stretch" spacing={3}>
								{articles.map((article) => (
									<Box
										key={article.id}
										p={4}
										borderWidth="1px"
										borderRadius="md"
										cursor="pointer"
										_hover={{ bg: "gray.50" }}
										onClick={() => {
											// Future: allow selecting specific article
										}}
									>
										<Text fontWeight="semibold">{article.title}</Text>
										<Text fontSize="sm" color="gray.600" noOfLines={2}>
											{article.content.substring(0, 150)}...
										</Text>
									</Box>
								))}
							</VStack>
						</Box>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default KnowledgeBaseSidebar;
